import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  TextField,
  Button,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { translations } from '../../locales/pl';

interface InviteUserDrawerProps {
  open: boolean;
  onClose: () => void;
  householdName: string;
  onInvite: (email: string) => Promise<void>;
}

const InviteUserDrawer: React.FC<InviteUserDrawerProps> = ({
  open,
  onClose,
  householdName,
  onInvite,
}) => {
  const [email, setEmail] = useState<string>('');
  const [inviting, setInviting] = useState<boolean>(false);

  useEffect(() => {
    if (open) {
      setEmail('');
    }
  }, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email.trim()) {
      return;
    }

    try {
      setInviting(true);
      await onInvite(email.trim());
      handleClose();
    } catch (error) {
      // Error handling is done in parent component
    } finally {
      setInviting(false);
    }
  };

  const handleClose = () => {
    setEmail('');
    onClose();
  };

  return (
    <Drawer anchor="bottom" open={open} onClose={handleClose}>
      <Box sx={{ padding: 3, minHeight: '300px' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 2,
          }}
        >
          <Typography variant="h6">
            {translations.households.inviteUser}
          </Typography>
          <IconButton onClick={handleClose} aria-label={translations.common.close}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            label={translations.households.householdName}
            value={householdName}
            disabled
            fullWidth
            sx={{ marginBottom: 2 }}
          />

          <TextField
            label={translations.households.emailAddress}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={inviting}
            fullWidth
            placeholder={translations.households.emailPlaceholder}
            sx={{ marginBottom: 3 }}
          />

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'space-between' }}>
            <Button onClick={handleClose} disabled={inviting}>
              {translations.common.cancel}
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={inviting || !email.trim()}
              startIcon={inviting ? <CircularProgress size={20} /> : null}
            >
              {inviting
                ? translations.households.sending
                : translations.households.sendInvitation}
            </Button>
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
};

export default InviteUserDrawer;
